import { z } from "zod";

import {
  PersonaBriefGenderSchema,
  PersonaBriefHookPackageSchema,
} from "../constants/persona-brief-options.js";

/**
 * Immutable marketplace snapshot of a persona brief at send time.
 * Survives later edits to the source PersonaBrief / version rows.
 */
export const MarketplaceBriefHookSnapshotSchema = z.object({
  hookIndex: z.number().int().min(1).max(4),
  direction: z.string(),
  line: z.string(),
});
export type MarketplaceBriefHookSnapshot = z.infer<
  typeof MarketplaceBriefHookSnapshotSchema
>;

export const MarketplaceBriefSnapshotSchema = z.object({
  kind: z.literal("PERSONA_BRIEF"),
  briefId: z.string().min(1),
  version: z.number().int().positive(),
  videoName: z.string(),
  duration: z.string(),
  hookPackage: PersonaBriefHookPackageSchema,
  selectedHookIndex: z.number().int().min(1).max(4),
  hooks: z.array(MarketplaceBriefHookSnapshotSchema).min(1).max(4),
  concept: z.object({
    direction: z.string(),
    line: z.string(),
  }),
  cta: z.object({
    direction: z.string(),
    line: z.string(),
  }),
  creatorProfile: z.object({
    ageRange: z.string(),
    gender: PersonaBriefGenderSchema,
    preferences: z.string().nullable(),
  }),
  brand: z.object({
    productLink: z.string().nullable(),
    font: z.string().nullable(),
    logo: z.string().nullable(),
    disclaimer: z.string().nullable(),
  }),
  notes: z.string().nullable(),
  angle: z.string().nullable().optional(),
  personaTitle: z.string().nullable().optional(),
  creativeExampleUrl: z.string().nullable().optional(),
  personaOpportunityId: z.string().min(1).optional(),
  opportunitySetId: z.string().min(1).nullable().optional(),
  opportunitySetName: z.string().nullable().optional(),
});
export type MarketplaceBriefSnapshot = z.infer<
  typeof MarketplaceBriefSnapshotSchema
>;

export function parseMarketplaceBriefSnapshot(
  value: unknown,
): MarketplaceBriefSnapshot | null {
  const parsed = MarketplaceBriefSnapshotSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function isMarketplaceBriefSnapshot(
  value: unknown,
): value is MarketplaceBriefSnapshot {
  return parseMarketplaceBriefSnapshot(value) != null;
}
